/**
 * Foundation plugin to build groups of Foundation togglers where only one group
 * member can be open at the same time. Needs the group id as data-toggler-group
 * attribute at the data-toggle elements.
 *
 * Attention: While Foundation Toggler is initialized on the toggle target, TogglerGroup
 * is initialized on the toggle button!
 */

import $ from 'jquery';
import { Foundation } from 'foundation-sites/js/foundation.core';

class TogglerGroup {
    constructor(element, options) {
        this.$element = element;
        this.options = $.extend({}, TogglerGroup.defaults, this.$element.data(), options);

        this.groupName = this.options.togglerGroup;
        this.$target = $(`#${this.options.toggle}`);

        // set listener on the target
        this.$target.on('on.zf.toggler.group', this._onOpen.bind(this));
        this.$target.on('off.zf.toggler.group', this._onClose.bind(this));

        // initially deactivate clicks if the target is active
        if (this.$element.attr('aria-expanded') === 'true') {
            this._clickOff();
        }
    }

    /**
     * Disable Clicks on the toggle.
     *
     * @private
     */
    _clickOff() {
        if (this.options.alwaysOneOpen) {
            this.$element.on('click.zf.trigger.group', (evt) => {
                evt.preventDefault();
                return false;
            });
        }
    }

    /**
     * Enable clicks on the toggle.
     *
     * @private
     */
    _clickOn() {
        if (this.options.alwaysOneOpen) {
            this.$element.off('click.zf.trigger.group');
        }
    }

    /**
     * Handler for the "on" event (target is opening).
     * Closes all other togglers of the same group.
     *
     * @private
     */
    _onOpen() {
        this._clickOff();

        // Get all open togglers in the group and close them (except those toggling the same target)
        const $groupTogglers = $(`[data-toggle][data-toggler-group='${this.groupName}']:not([data-toggle='${this.$element[0].dataset.toggle}'])`);
        if ($groupTogglers.length) {
            $groupTogglers.foundation('close');
        }
    }

    /**
     * Handler for the "off" event (target is closing).
     *
     * @private
     */
    _onClose() {
        this._clickOn();
    }

    /**
     * Close the target.
     *
     */
    close() {
        // eslint-disable-next-line func-names
        const $openContents = this.$target.filter(function () {
            return $(this).data('zfPlugin').isOn;
        });

        if ($openContents.length) {
            $openContents.foundation('toggle');
        }
    }

    /**
     * Unbind event handlers
     */
    _destroy() {
        if (this.$target) {
            this.$target.off('on.zf.toggler.group');
            this.$target.off('off.zf.toggler.group');
        }
        if (this._clickOn) {
            this._clickOn();
        }
    }
}

/**
 * Default settings
 *
 * @type {{alwaysOneOpen: boolean}}
 */
TogglerGroup.defaults = {
    alwaysOneOpen: false,
};

Foundation.plugin(TogglerGroup, 'TogglerGroup');
